import { defineStore } from 'pinia'
import { GAME_VERSIONS, getDefaultGameVersion, getGameVersionById } from '../config/gameVersions'

/**
 * Game Version Store
 *
 * Centralized store for managing the currently selected game version.
 * This affects content across the entire application.
 */
export const useGameVersionStore = defineStore('gameVersion', {
  state: () => ({
    // Start with the default version (typically D&D 5E)
    currentVersionId: getDefaultGameVersion().id,
    // This tracks if we're using fallback version due to API limitations
    usingFallback: false,
    // The fallback version ID (typically D&D 5E as it has the most complete API)
    fallbackVersionId: GAME_VERSIONS.DND_5E.id,
    // Game version options for the dropdown - populated in an action
    gameVersionOptions: [],
    // Track if the selected version has no API support
    noApiSupport: false,
    // Custom game versions added by the user
    customGameVersions: []
  }),

  getters: {
    /**
     * Get the current version configuration object
     */
    currentVersion: (state) => {
      // First check if it's a custom version
      const customVersion = state.customGameVersions.find((v) => v.id === state.currentVersionId)
      if (customVersion) {
        return {
          id: customVersion.id,
          displayName: customVersion.label,
          description: customVersion.description,
          apiStatus: 'CUSTOM',
          gameSystem: 'CUSTOM'
        }
      }
      // If not custom, get from predefined versions
      return getGameVersionById(state.currentVersionId)
    },

    /**
     * Get the display name of the current version
     */
    currentVersionName: (state) => {
      // Check if it's a custom version
      const customVersion = state.customGameVersions.find((v) => v.id === state.currentVersionId)
      if (customVersion) {
        return customVersion.label
      }
      // If not custom, get from predefined versions
      const version = getGameVersionById(state.currentVersionId)
      return version ? version.displayName : 'Unknown Version'
    },

    /**
     * Get the actual version being used, accounting for fallbacks
     */
    effectiveVersionId: (state) =>
      state.usingFallback ? state.fallbackVersionId : state.currentVersionId,

    /**
     * Get the effective version configuration
     */
    effectiveVersion: (state) => {
      const versionId = state.usingFallback ? state.fallbackVersionId : state.currentVersionId
      return getGameVersionById(versionId)
    },

    /**
     * Check if the current version has API support
     */
    hasApiSupport: (state) => {
      const version = getGameVersionById(state.currentVersionId)
      return version && (version.apiStatus === 'ACTIVE' || version.apiStatus === 'PARTIAL')
    }
  },

  actions: {
    /**
     * Initialize the game version options for dropdowns
     * @param {boolean} includeAll - Whether to include all versions or just active ones
     * @param {string} gameSystem - Optional game system filter
     * @param {boolean} onlyWithCharacterSheets - Whether to only include versions with character sheets
     * @param {boolean} onlyCompleteAPI - Whether to only include versions with ACTIVE API status
     */
    initGameVersionOptions(
      includeAll = true,
      gameSystem = null,
      onlyWithCharacterSheets = false,
      onlyCompleteAPI = false
    ) {
      const options = Object.values(GAME_VERSIONS)
        .filter((version) => {
          // Filter by game system if specified
          if (gameSystem && version.gameSystem !== gameSystem) return false

          // Filter by character sheet implementation if specified
          if (onlyWithCharacterSheets && !version.hasCharacterSheet) return false

          // Filter by complete API implementation if specified
          if (onlyCompleteAPI && version.apiStatus !== 'ACTIVE') return false

          // Include all or filter by API status
          return includeAll || version.apiStatus !== 'NONE'
        })
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((version) => ({
          label: version.displayName,
          value: version.id,
          id: version.id,
          description: version.description,
          disabled: version.apiStatus === 'NONE' || version.apiStatus === 'PLANNED',
          apiStatus: version.apiStatus,
          iconPath: version.iconPath,
          hasApiSpec: !!version.apiSpecPath,
          hasCharacterSheet: version.hasCharacterSheet
        }))

      this.gameVersionOptions = options

      // Load saved custom versions
      this.loadCustomVersions()
    },

    /**
     * Set the current game version
     * @param {string} versionId - The game version ID to use
     */
    async setGameVersion(versionId) {
      // If switching to a different version, clean up the old version's cache
      if (this.currentVersionId !== versionId) {
        try {
          // Clear caches for the old version (lazy import to avoid build-time cycles)
          const mod = await import('../utils/globalCacheManager')
          await mod.clearGameVersionCaches(this.currentVersionId)
        } catch (error) {
          console.error(`Error clearing cache for version ${this.currentVersionId}:`, error)
        }
      }

      this.currentVersionId = versionId
      this.usingFallback = false

      // No external API service call here — the app's stores will decide whether
      // to consult Firestore or local caches when fetching resources.

      // Save the preference in localStorage for persistence
      localStorage.setItem('preferredGameVersion', versionId)
    },

    /**
     * Add a custom game version created by the user
     * @param {Object} customVersion - The custom version object
     */
    addCustomGameVersion(customVersion) {
      // First check if this custom version already exists
      const exists = this.customGameVersions.some((v) => v.id === customVersion.id)
      if (!exists) {
        this.customGameVersions.push(customVersion)
        this.saveCustomVersions()
      }
    },

    /**
     * Remove a custom game version
     * @param {string} customVersionId - The ID of the custom version to remove
     */
    removeCustomGameVersion(customVersionId) {
      this.customGameVersions = this.customGameVersions.filter((v) => v.id !== customVersionId)
      this.saveCustomVersions()

      // If we were using this version, switch to default
      if (this.currentVersionId === customVersionId) {
        this.setGameVersion(getDefaultGameVersion().id)
      }
    },

    /**
     * Save custom versions to localStorage
     */
    saveCustomVersions() {
      localStorage.setItem('customGameVersions', JSON.stringify(this.customGameVersions))
    },

    /**
     * Load custom versions from localStorage
     */
    loadCustomVersions() {
      try {
        const savedCustomVersions = localStorage.getItem('customGameVersions')
        if (savedCustomVersions) {
          this.customGameVersions = JSON.parse(savedCustomVersions)
        }
      } catch (error) {
        console.error('Error loading custom game versions:', error)
        // If there's an error, reset to empty array
        this.customGameVersions = []
      }
    },

    /**
     * Set fallback version when the selected version API fails
     * @param {string} fallbackId - The ID of the fallback version to use
     */
    setFallbackMode(fallbackId) {
      this.usingFallback = true
      if (fallbackId) {
        this.fallbackVersionId = fallbackId
      }
    },

    /**
     * Clear fallback mode, reverting to using the selected version
     */
    clearFallbackMode() {
      this.usingFallback = false
    },

    /**
     * Load saved game version preference from localStorage
     */
    loadSavedVersion() {
      const savedVersion = localStorage.getItem('preferredGameVersion')
      if (savedVersion) {
        this.setGameVersion(savedVersion)
      }
    },

    /**
     * Set the API support status flag
     * @param {boolean} status - Whether the current version has no API support
     */
    setNoApiSupport(status) {
      this.noApiSupport = status
    }
  }
})
